// frontend/src/components/client/ClientInvoices.jsx
import React, { useState, useEffect } from 'react';
import { FileText, Download, Eye, CreditCard, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { getInvoices, getInvoiceStats } from '../../services/clientApi';

const StatusBadge = ({ status }) => {
  const config = {
    pending: { label: 'Pending', color: 'bg-yellow-500/20 text-yellow-400', icon: Clock },
    paid: { label: 'Paid', color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
    overdue: { label: 'Overdue', color: 'bg-red-500/20 text-red-400', icon: AlertCircle }
  };
  const { label, color, icon: Icon } = config[status] || config.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${color}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
};

const ClientInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadData();
  }, [filter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [invoicesRes, statsRes] = await Promise.all([
        getInvoices(filter === 'all' ? null : filter),
        getInvoiceStats()
      ]);
      setInvoices(invoicesRes.data.invoices || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error loading invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const StatCard = ({ title, value, color }) => (
    <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
      <p className="text-gray-400 text-sm">{title}</p>
      <p className={`text-2xl font-bold ${color} mt-1`}>{value}</p>
    </div>
  );

  return (
    <div className="px-4 sm:px-0">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-white">Invoices</h2>
        <p className="text-gray-400 text-sm">View and manage your billing history</p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
          <StatCard title="Pending" value={formatCurrency(stats.pending?.total)} color="text-yellow-400" />
          <StatCard title="Paid" value={formatCurrency(stats.paid?.total)} color="text-green-400" />
          <StatCard title="Total Invoices" value={stats.pending?.count + stats.paid?.count} color="text-blue-400" />
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2 mb-4 overflow-x-auto pb-2">
        {['all', 'pending', 'paid'].map((tab) => (
          <button
            key={tab}
            onClick={() => setFilter(tab)}
            className={`px-4 py-1.5 rounded-lg text-sm transition ${
              filter === tab
                ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Invoices List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      ) : invoices.length === 0 ? (
        <div className="text-center py-12 bg-gray-800/50 rounded-xl border border-gray-700">
          <FileText className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-500">No invoices found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {invoices.map((inv) => (
            <div key={inv.id} className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-500/20 rounded-lg">
                    <FileText className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="text-white font-medium">#{inv.invoice_number}</span>
                      <StatusBadge status={inv.status} />
                    </div>
                    <p className="text-sm text-gray-400">{formatDate(inv.created_at)}</p>
                    <p className="text-lg font-bold text-white mt-1">{formatCurrency(inv.amount)}</p>
                    {inv.due_date && (
                      <p className="text-xs text-gray-500 mt-1">
                        Due: {formatDate(inv.due_date)}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 self-end sm:self-center">
                  <button className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 transition">
                    <Eye className="w-4 h-4" />
                  </button>
                  <button className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 transition">
                    <Download className="w-4 h-4" />
                  </button>
                  {inv.status === 'pending' && (
                    <button className="px-3 py-2 bg-green-500 hover:bg-green-600 rounded-lg text-white text-sm flex items-center gap-1">
                      <CreditCard className="w-4 h-4" />
                      Pay Now
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClientInvoices;